import SearchInput from '../../../components/ui/SearchInput';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  allCourses,
  getPopularCategoryCourses,
  getRecommendedCourses,
  getRecommendationPreferences,
  saveRecommendationPreferences,
  skipRecommendationPreferences,
  trackRecommendationEvent,
} from '../../../services/user/course';
import { useOwnedCourseState } from '../../../hooks/useOwnedCourseIds';
import useCourseCardProgress from '../../../hooks/useCourseCardProgress';
import { getCourseCardState } from '../../../utils/courseCardState';
import NoData from '../../../components/common/NoData';

import { encodeData } from '../../../utils/encodeAndEncode';
import { mapRecommendedCourses } from './courseRecommendation.utils';
import CourseCatalogCard from '../../../components/user/CourseCatalogCard';
import { getRecommendationSectionCopy } from './recommendationPresentation.utils';
import RecommendationPreferencesModal from '../../../components/user/RecommendationPreferencesModal';
import RecommendationEmptyState from '../../../components/user/RecommendationEmptyState';
import { notifyRecommendationPreferencesUpdated } from '../../../utils/recommendationPreferences';

const SESSION_SKIP_KEY = 'recommendation_prefs_skipped_session';

function CourseTab() {
  const { status: ownedCoursesStatus, courses: ownedCourses } = useOwnedCourseState();
  const navigate = useNavigate();
  const observer = useRef();
  const searchTimeout = useRef(null);
  const mobileFilterRef = useRef(null);
  const highlightScrollRef = useRef(null);
  const popularCategoryScrollRefs = useRef({});
  const catalogScrollRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [popularCategories, setPopularCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isRecommendedLoading, setIsRecommendedLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMobileCategoryFilter, setShowMobileCategoryFilter] = useState(false);
  const [recommendationMeta, setRecommendationMeta] = useState({
    is_new_user: false,
    strategy: 'personalized',
    new_user_reason: null,
    preferences_applied: false,
    needs_onboarding: false,
  });
  const [clickedCourseId, setClickedCourseId] = useState(null);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [availablePreferenceCategories, setAvailablePreferenceCategories] = useState([]);
  const [preselectedCategories, setPreselectedCategories] = useState([]);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [activeMobileSection, setActiveMobileSection] = useState('catalog');
  const currentUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.id || 'anonymous';
    } catch (error) {
      return 'anonymous';
    }
  })();
  const sessionSkipKey = `${SESSION_SKIP_KEY}:${currentUserId}`;

  const toCategoryArray = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item || '').trim()).filter(Boolean);
        }
      } catch (error) {
        return [trimmed];
      }

      return [trimmed];
    }

    return [];
  };

  const mapCatalogCourse = (course, metadata = {}) => ({
    id: course.id,
    image: course.banner_image,
    title: course.course_name || 'Untitled Course',
    description: course.course_desc || 'No description provided.',
    modules: metadata.totalModules || metadata.total_modules || 0,
    enrolled: metadata.totalUsers || metadata.total_users || 0,
    duration: metadata.duration || '—',
    rating: course.total_rating || 0,
    level: course.levels || 'N/A',
    category: toCategoryArray(course.course_categories).join(', ') || 'General',
    original_course_category: toCategoryArray(course.course_categories),
    price: course.course_type?.toLowerCase() === 'free' ? 'Free' : course.course_price || '0.00',
    course_type: course.course_type,
    currency: course.currency || 'USD',
    course_url: course.course_url || null,
    is_public: Boolean(course.is_public),
    organization_name: course.organization_name || '',
  });

  const fetchCourses = async (pageNum = 1, reset = false, search = '') => {
    try {
      setIsLoading(true);
      if (pageNum === 1) setLoadError(false);
      const start = Date.now();
      const response = await allCourses({ page: pageNum, limit: 10, search });
      const elapsed = Date.now() - start;
      const delay = 500 - elapsed;

      const process = () => {
        if (response.success && response.data?.courses) {
          const mappedCourses = response.data.courses.map((course, index) =>
            mapCatalogCourse(course, response.data.metadata?.[index] || {})
          );
          setCourses((prev) => (reset ? mappedCourses : [...prev, ...mappedCourses]));
          setHasMore(pageNum < response.data.totalPages);
        } else {
          setHasMore(false);
          if (pageNum === 1) setLoadError(true);
        }
        setIsLoading(false);
      };

      if (delay > 0) {
        setTimeout(process, delay);
      } else {
        process();
      }
    } catch (error) {
      if (pageNum === 1) setLoadError(true);
      setIsLoading(false);
    }
  };

  const fetchRecommendedCourses = async () => {
    try {
      setIsRecommendedLoading(true);
      const response = await getRecommendedCourses(4);
      const recommendations = response?.data?.recommendations || [];
      const meta = response?.data?.meta || {
        is_new_user: false,
        strategy: 'personalized',
        new_user_reason: null,
        preferences_applied: false,
        needs_onboarding: false,
      };
      const mappedRecommendations = mapRecommendedCourses(recommendations);
      setRecommendedCourses(mappedRecommendations);
      setRecommendationMeta(meta);

      if (recommendations.length > 0) {
        await Promise.all(
          recommendations.map((course) =>
            trackRecommendationEvent({
              event_type: 'impression',
              course_id: course.id,
              surface: 'course_catalog_recommended',
            })
          )
        );
      }
    } catch (error) {
      setRecommendedCourses([]);
      setRecommendationMeta({
        is_new_user: false,
        strategy: 'personalized',
        new_user_reason: null,
        preferences_applied: false,
        needs_onboarding: false,
      });
    } finally {
      setIsRecommendedLoading(false);
    }
  };

  const fetchPopularCategoryCourses = async () => {
    try {
      const response = await getPopularCategoryCourses({ categoryLimit: 3, courseLimit: 4 });
      const categoryGroups = (response?.data?.categories || [])
        .map((category) => ({
          categoryName: category.category_name,
          totalEnrollments: category.total_enrollments || 0,
          courses: (category.courses || []).map((course) => mapCatalogCourse(course, course.metadata || {})),
        }))
        .filter((category) => category.courses.length > 0);

      setPopularCategories(categoryGroups);
    } catch (error) {
      setPopularCategories([]);
    }
  };

  const fetchRecommendationOnboardingState = async () => {
    try {
      const response = await getRecommendationPreferences();
      const data = response?.data || {};

      setAvailablePreferenceCategories(data.available_categories || []);
      setPreselectedCategories(data.preferred_category_ids || []);

      const isSessionSkipped = sessionStorage.getItem(sessionSkipKey) === '1';
      setShowPreferencesModal(Boolean(data.needs_onboarding) && !isSessionSkipped);
    } catch (error) {
      setShowPreferencesModal(false);
    }
  };

  const handleSavePreferences = async (payload) => {
    try {
      setIsSavingPreferences(true);
      const response = await saveRecommendationPreferences(payload);
      if (response?.success !== false) {
        sessionStorage.removeItem(sessionSkipKey);
        setShowPreferencesModal(false);
        notifyRecommendationPreferencesUpdated();
        await fetchRecommendedCourses();
      }
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const handleSkipPreferences = async () => {
    try {
      setIsSavingPreferences(true);
      await skipRecommendationPreferences();
    } finally {
      sessionStorage.setItem(sessionSkipKey, '1');
      setShowPreferencesModal(false);
      setIsSavingPreferences(false);
    }
  };

  useEffect(() => {
    fetchCourses(1, true, searchTerm);
    fetchRecommendedCourses();
    fetchPopularCategoryCourses();
    fetchRecommendationOnboardingState();
    setPage(1);
  }, []);

  useEffect(() => {
    const isSessionSkipped = sessionStorage.getItem(sessionSkipKey) === '1';
    if (recommendationMeta.needs_onboarding && !isSessionSkipped) {
      setShowPreferencesModal(true);
    }
  }, [recommendationMeta.needs_onboarding, sessionSkipKey]);

  useEffect(() => {
    if (page > 1) fetchCourses(page, false, searchTerm);
  }, [page]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!mobileFilterRef.current?.contains(event.target)) {
        setShowMobileCategoryFilter(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lastCourseRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleCourseClick = (course, surface = 'course_catalog', resultPosition = null) => {
    setClickedCourseId(course.id);
    const encoded = encodeData({ id: course.id });

    trackRecommendationEvent({
      event_type: 'click',
      course_id: course.id,
      surface,
      search_query: searchTerm.trim() || null,
      result_position: resultPosition,
    }).catch(() => {});

    navigate(`/user/courses/${ownedCourses.has(String(course.id)) ? 'WatchCourse' : 'courseDetails'}/${encodeURIComponent(encoded)}`);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setCourses([]);
      setPage(1);
      fetchCourses(1, true, value);
    }, 500);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setShowMobileCategoryFilter(false);
  };

  const scrollCourseSection = (targetRef, direction) => {
    const node = targetRef?.current;
    if (!node) return;

    const firstItem = node.querySelector('.mobile-horizontal-item');
    const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : node.clientWidth / 2;
    const gapValue = window.getComputedStyle(node).columnGap || window.getComputedStyle(node).gap || '0';
    const gap = Number.parseFloat(gapValue) || 0;
    const cardsPerScroll = window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 4;
    const scrollDistance = (itemWidth + gap) * cardsPerScroll;

    node.scrollBy({
      left: direction === 'right' ? scrollDistance : -scrollDistance,
      behavior: 'smooth',
    });
  };

  const filteredCourses = selectedCategory === 'all'
    ? courses
    : courses.filter((course) => course.original_course_category.includes(selectedCategory));
  const categoryOptions = ['all', ...new Set(courses.flatMap((course) => course.original_course_category).filter(Boolean))];

  const searchValue = searchTerm.trim().toLowerCase();
  const matchesSearchAndCategory = (course) => {
    const matchesCategory =
      selectedCategory === 'all' || course.original_course_category.includes(selectedCategory);
    if (!matchesCategory) return false;
    if (!searchValue) return true;
    const searchableText = `${course.title} ${course.description} ${course.category || ''}`.toLowerCase();
    return searchableText.includes(searchValue);
  };

  const isCatalogFiltered = Boolean(searchValue) || selectedCategory !== 'all';
  const visibleCardsPerView = viewportWidth < 640 ? 1 : viewportWidth < 1024 ? 2 : 4;
  const highlightCourses = recommendedCourses;
  const shouldShowHighlightSection = !isCatalogFiltered;
  const shouldShowPopularCategoriesSection = !isCatalogFiltered && popularCategories.length > 0;
  const filteredCatalogCourses = filteredCourses.filter(matchesSearchAndCategory);
  const displayedCourses = [
    ...highlightCourses,
    ...popularCategories.flatMap((category) => category.courses),
    ...filteredCatalogCourses,
  ];
  const courseProgress = useCourseCardProgress(
    displayedCourses
      .filter((course) => ownedCoursesStatus === 'ready' &&
        ownedCourses.has(String(course.id)) && Number(ownedCourses.get(String(course.id)).is_completed) !== 1)
      .map((course) => course.id)
  );
  const getCatalogCardState = (course) => getCourseCardState(
    ownedCourses.get(String(course.id)),
    ownedCoursesStatus,
    courseProgress[String(course.id)]
  );
  const highlightHasEnrolledCourses = ownedCoursesStatus === 'ready' &&
    highlightCourses.some((course) => ownedCourses.has(String(course.id)));
  const catalogHasEnrolledCourses = ownedCoursesStatus === 'ready' &&
    filteredCatalogCourses.some((course) => ownedCourses.has(String(course.id)));
  const shouldShowCatalogSection = filteredCatalogCourses.length > 0 || !isLoading || isLoading;
  const recommendationCopy = getRecommendationSectionCopy(recommendationMeta, 'course_tab');
  const highlightTitle = recommendationCopy.title;
  const highlightDescription = recommendationCopy.description;
  const mobileSections = [
    {
      key: 'recommended',
      label: 'Recommended',
      visible: shouldShowHighlightSection,
    },
    {
      key: 'popular',
      label: 'Popular',
      visible: shouldShowPopularCategoriesSection,
    },
    {
      key: 'catalog',
      label: 'All Courses',
      visible: shouldShowCatalogSection || isLoading,
    },
  ].filter((section) => section.visible);
  const shouldShowSectionSwitcher = !isCatalogFiltered && mobileSections.length > 1;

  useEffect(() => {
    if (!shouldShowSectionSwitcher) {
      if (activeMobileSection !== 'catalog') {
        setActiveMobileSection('catalog');
      }
      return;
    }

    const activeStillVisible = mobileSections.some((section) => section.key === activeMobileSection);
    if (!activeStillVisible) {
      setActiveMobileSection(mobileSections[0]?.key || 'catalog');
    }
  }, [shouldShowSectionSwitcher, mobileSections, activeMobileSection]);

  const showHighlightBlock = shouldShowHighlightSection && (!shouldShowSectionSwitcher || activeMobileSection === 'recommended');
  const showPopularCategoriesBlock =
    shouldShowPopularCategoriesSection && (!shouldShowSectionSwitcher || activeMobileSection === 'popular');
  const showCatalogBlock = shouldShowCatalogSection && (!shouldShowSectionSwitcher || activeMobileSection === 'catalog');
  const showHighlightNav = highlightCourses.length > visibleCardsPerView;
  // "All Courses" is a wrapping grid (every course visible), so no carousel arrows needed
  const showCatalogNav = false;

  return (
    <div className="course-tab-content">
      <RecommendationPreferencesModal
        show={showPreferencesModal}
        availableCategories={availablePreferenceCategories}
        initialCategories={preselectedCategories}
        onSave={handleSavePreferences}
        onSkip={handleSkipPreferences}
        isSaving={isSavingPreferences}
      />
      <div className="tw-flex tw-items-center tw-gap-[0.65rem] tw-w-full max-[768px]:tw-items-stretch course-toolbar tw-mt-2 tw-mb-4">
        <div className="[flex:0_1_300px] tw-min-w-0 tw-max-w-[300px] max-[768px]:[flex:1_1_auto] max-[768px]:tw-max-w-none course-toolbar-search tw-relative">
          <SearchInput
            type="text"
            className={"max-[768px]:tw-min-h-[46px] max-[768px]:tw-text-[0.95rem] !tw-pl-[35px] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjExIiBjeT0iMTEiIHI9IjgiPjwvY2lyY2xlPjxsaW5lIHgxPSIyMSIgeTE9IjIxIiB4Mj0iMTYuNjUiIHkyPSIxNi42NSI+PC9saW5lPjwvc3ZnPg==)] tw-bg-no-repeat [transition:all_0.2s_ease] tw-p-[12px_16px] tw-border-solid tw-bg-[white] tw-text-[14px] [&:focus]:tw-shadow-[0_0_0_3px_rgba(74,_108,_247,_0.1)] [&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input search-input course-search-input"}
            placeholder="Search courses..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <div className="[flex:0_0_auto] tw-min-w-[220px] tw-ml-auto max-[768px]:tw-hidden course-toolbar-filter-desktop">
          <select
            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [appearance:none] tw-pe-[2.25rem] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyMCcgaGVpZ2h0PScyMCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjNjQ3NDhiJyB2aWV3Qm94PScwIDAgMjQgMjQnPjxwYXRoIGQ9J203IDEwIDUgNSA1LTUnLz48L3N2Zz4=)] tw-bg-[right_.65rem_center] tw-bg-no-repeat [&[multiple]]:tw-bg-[none] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-select"}
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            aria-label="Filter by category"
          >
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category === 'all' ? 'All categories' : category}
              </option>
            ))}
          </select>
        </div>

        <div className="tw-hidden tw-relative max-[768px]:tw-block course-toolbar-filter-mobile" ref={mobileFilterRef}>
          <button
            type="button"
            className={"tw-w-[42px] tw-h-[42px] [&_svg]:tw-text-[1.2rem] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-outline-secondary course-filter-icon-btn"}
            onClick={() => setShowMobileCategoryFilter((prev) => !prev)}
            aria-label="Open category filter"
            aria-expanded={showMobileCategoryFilter}
          >
            <Icon icon="mdi:filter-variant" />
          </button>
          {showMobileCategoryFilter && (
            <div className="tw-absolute tw-top-[calc(100%_+_8px)] tw-right-0 tw-min-w-[220px] tw-max-w-[280px] tw-bg-[#fff] tw-border-[1px] tw-border-solid tw-border-[#dfe6f4] tw-rounded-[12px] tw-shadow-[0_14px_26px_rgba(15,_23,_42,_0.14)] tw-z-[50] tw-p-3 course-mobile-filter-popup">
              <div className="tw-text-[0.84rem] tw-font-semibold tw-text-[#445172] tw-mb-[0.55rem] course-mobile-filter-title">Filter by category</div>
              <div className="tw-flex tw-flex-col tw-gap-[0.35rem] tw-max-h-[220px] tw-overflow-y-auto course-mobile-filter-options">
                {categoryOptions.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`tw-border-[1px] tw-border-solid tw-border-[#d8e2f4] tw-bg-[#fff] tw-text-[#2d3a57] tw-rounded-[8px] tw-p-[0.4rem_0.55rem] tw-text-[0.82rem] tw-text-left [&.active]:tw-border-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-bg-[#eef2ff] course-mobile-filter-option   ${
                      selectedCategory === category ? "active" : ""
                    }`}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category === 'all' ? 'All categories' : category}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {shouldShowSectionSwitcher && (
        <div
          className={`tw-flex tw-gap-[0.55rem] tw-overflow-x-auto tw-pb-[0.2rem] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden [&.is-desktop]:tw-justify-start [&.is-desktop]:tw-flex-wrap course-mobile-section-switcher course-section-switcher tw-mb-4   ${isMobileView ? "is-mobile" : "is-desktop"}`}
          role="tablist"
          aria-label="Course discovery sections"
        >
          {mobileSections.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`[flex:0_0_auto] tw-border-[1px] tw-border-solid tw-border-[#d8e2f4] tw-bg-[#ffffff] tw-text-[#4c5a77] tw-rounded-[999px] tw-p-[0.45rem_0.85rem] tw-text-[0.82rem] tw-font-semibold tw-shadow-[0_6px_16px_rgba(15,_23,_42,_0.06)] [&.active]:tw-border-[#3152e8] [&.active]:tw-bg-[#3152e8] [&.active]:tw-text-[#ffffff] course-mobile-section-chip   ${activeMobileSection === section.key ? "active" : ""}`}
              onClick={() => setActiveMobileSection(section.key)}
              aria-pressed={activeMobileSection === section.key}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}

      {showHighlightBlock && (
        <div className="tw-p-[0.85rem] tw-border-[1px] tw-border-solid tw-border-[#e8edf8] tw-rounded-[18px] [background:linear-gradient(180deg,_#f8fbff_0%,_#ffffff_100%)] recommended-course-section tw-mb-6">
          <div className="tw-mb-4">
            <h5 className="![font-weight:var(--font-weight-semibold)] tw-mb-1 tw-font-semibold">{highlightTitle}</h5>
            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">{highlightDescription}</p>
          </div>

          {isRecommendedLoading && (
            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Loading recommendations...</p>
          )}

          {!isRecommendedLoading && highlightCourses.length === 0 && (
            <RecommendationEmptyState
              onBrowseCourses={() => {
                setActiveMobileSection('catalog');
                navigate('/user/courses?tab=courses');
              }}
              onUpdateInterests={() => navigate('/user/profile?tab=interests')}
            />
          )}

          {!isRecommendedLoading && highlightCourses.length > 0 && (
            <div className="tw-relative tw-p-[0_0.9rem] mobile-carousel-shell">
              {showHighlightNav && (
                <button
                  type="button"
                  className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-left-0 mobile-carousel-nav mobile-carousel-nav--left"}
                  onClick={() => scrollCourseSection(highlightScrollRef, 'left')}
                  aria-label="Scroll recommended courses left"
                >
                  <Icon icon="mdi:chevron-left" />
                </button>
              )}
              <div className={"tw-m-0 tw-flex tw-flex-nowrap tw-overflow-x-auto tw-gap-3 tw-items-stretch tw-justify-start [scroll-behavior:smooth] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden max-[768px]:tw-gap-[0.6rem] max-[768px]:tw-pb-[0.3rem] max-[768px]:tw-pl-0 max-[768px]:tw-pr-0 mobile-horizontal-grid"} ref={highlightScrollRef}>
                {highlightCourses.map((course) => (
                  <div key={`recommended-${course.id}`} className={"[flex:0_0_calc((100%_-_2.25rem)_/_4)] tw-w-[calc((100%_-_2.25rem)_/_4)] tw-max-w-[calc((100%_-_2.25rem)_/_4)] tw-min-w-[0] [&_.course-card]:tw-h-full max-[768px]:[flex:0_0_calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-max-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-min-w-[0] max-[768px]:tw-mb-0 max-[768px]:tw-p-0 max-[768px]:[&_.course-card]:tw-rounded-[12px] max-[768px]:[&_.course-card]:tw-min-h-full max-[768px]:[&_.course-card]:tw-flex max-[768px]:[&_.course-card]:tw-flex-col max-[768px]:[&_.course-card--compact_.course-image]:tw-pt-[46%] max-[768px]:[&_.course-card--compact_.course-details]:tw-p-[0.55rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-text-[0.82rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-mb-[0.3rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-text-[0.75rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-leading-[1.35] max-[768px]:[&_.course-card--compact_.course-description]:tw-h-[3.95em] max-[768px]:[&_.course-card--compact_.course-description]:tw-mb-[0.45rem] max-[768px]:[&_.recommendation-reason]:tw-text-[0.72rem] max-[768px]:[&_.recommendation-reason]:tw-leading-[1.35] max-[768px]:[&_.recommendation-reason]:tw-p-[0.45rem_0.5rem] max-[768px]:[&_.recommendation-reason]:tw-mb-[0.45rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-text-[0.75rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-mb-1 max-[768px]:[&_.meta-row_.ui-button]:!tw-text-[0.68rem] max-[768px]:[&_.meta-row_.ui-button]:!tw-p-[0.27rem_0.42rem] max-[768px]:[&_.course-footer]:tw-pt-[0.42rem] max-[768px]:[&_.course-footer]:tw-items-center max-[768px]:[&_.course-price]:tw-text-[0.82rem] max-[768px]:[&_.watch-now-btn]:tw-min-w-[0] max-[768px]:[&_.watch-now-btn]:tw-text-[0.72rem] max-[768px]:[&_.watch-now-btn]:tw-p-[0.3rem_0.5rem] max-[768px]:[&_.watch-now-btn]:tw-gap-[0.2rem] mobile-horizontal-item"}>
                    <CourseCatalogCard
                      course={course}
                      {...getCatalogCardState(course)}
                      reserveProgressSpace={highlightHasEnrolledCourses}
                      onPrimaryAction={(selectedCourse) =>
                        handleCourseClick(
                          selectedCourse,
                          'course_catalog_recommended'
                        )
                      }
                      showRecommendation
                      showRecommendationPill
                      recommendationText={course.reason}
                      isPrimaryLoading={clickedCourseId === course.id}
                      compact
                    />
                  </div>
                ))}
              </div>
              {showHighlightNav && (
                <button
                  type="button"
                  className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-right-0 mobile-carousel-nav mobile-carousel-nav--right"}
                  onClick={() => scrollCourseSection(highlightScrollRef, 'right')}
                  aria-label="Scroll recommended courses right"
                >
                  <Icon icon="mdi:chevron-right" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showPopularCategoriesBlock && (
        <div className="tw-p-[0.85rem] tw-border-[1px] tw-border-solid tw-border-[#e8edf8] tw-rounded-[18px] [background:linear-gradient(180deg,_#f8fbff_0%,_#ffffff_100%)] recommended-course-section tw-mb-6">
          <div className="tw-mb-4">
            <h5 className="![font-weight:var(--font-weight-semibold)] tw-mb-1 tw-font-semibold">Popular Categories</h5>
            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Most-enrolled categories across the catalog.</p>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-6">
            {popularCategories.map((category) => (
              <div key={`popular-category-${category.categoryName}`} className="popular-category-group">
                {(() => {
                  const showPopularNav = category.courses.length > visibleCardsPerView;

                  return (
                    <>
                <div className="tw-flex tw-justify-between tw-items-center tw-flex-wrap tw-gap-2 tw-mb-4">
                  <div>
                    <h6 className="![font-weight:var(--font-weight-semibold)] tw-mb-1 tw-font-semibold">{`Popular in ${category.categoryName}`}</h6>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Most-enrolled courses in this category across the catalog.</p>
                  </div>
                  <span className="tw-inline-flex tw-items-center tw-p-[0.4rem_0.8rem] tw-rounded-[999px] tw-bg-[#ecf3ff] tw-text-[#3152e8] tw-text-[0.85rem] tw-font-semibold recommended-course-hint">{`${category.totalEnrollments} enrollments`}</span>
                </div>

                <div className="tw-relative tw-p-[0_0.9rem] mobile-carousel-shell">
                  {showPopularNav && (
                    <button
                      type="button"
                      className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-left-0 mobile-carousel-nav mobile-carousel-nav--left"}
                      onClick={() =>
                        scrollCourseSection(
                          { current: popularCategoryScrollRefs.current[category.categoryName] },
                          'left'
                        )
                      }
                      aria-label={`Scroll popular ${category.categoryName} courses left`}
                    >
                      <Icon icon="mdi:chevron-left" />
                    </button>
                  )}
                  <div
                    className={"tw-m-0 tw-flex tw-flex-nowrap tw-overflow-x-auto tw-gap-3 tw-items-stretch tw-justify-start [scroll-behavior:smooth] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden max-[768px]:tw-gap-[0.6rem] max-[768px]:tw-pb-[0.3rem] max-[768px]:tw-pl-0 max-[768px]:tw-pr-0 mobile-horizontal-grid"}
                    ref={(node) => {
                      popularCategoryScrollRefs.current[category.categoryName] = node;
                    }}
                  >
                    {category.courses.map((course) => (
                      <div key={`popular-${category.categoryName}-${course.id}`} className={"[flex:0_0_calc((100%_-_2.25rem)_/_4)] tw-w-[calc((100%_-_2.25rem)_/_4)] tw-max-w-[calc((100%_-_2.25rem)_/_4)] tw-min-w-[0] [&_.course-card]:tw-h-full max-[768px]:[flex:0_0_calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-max-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-min-w-[0] max-[768px]:tw-mb-0 max-[768px]:tw-p-0 max-[768px]:[&_.course-card]:tw-rounded-[12px] max-[768px]:[&_.course-card]:tw-min-h-full max-[768px]:[&_.course-card]:tw-flex max-[768px]:[&_.course-card]:tw-flex-col max-[768px]:[&_.course-card--compact_.course-image]:tw-pt-[46%] max-[768px]:[&_.course-card--compact_.course-details]:tw-p-[0.55rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-text-[0.82rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-mb-[0.3rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-text-[0.75rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-leading-[1.35] max-[768px]:[&_.course-card--compact_.course-description]:tw-h-[3.95em] max-[768px]:[&_.course-card--compact_.course-description]:tw-mb-[0.45rem] max-[768px]:[&_.recommendation-reason]:tw-text-[0.72rem] max-[768px]:[&_.recommendation-reason]:tw-leading-[1.35] max-[768px]:[&_.recommendation-reason]:tw-p-[0.45rem_0.5rem] max-[768px]:[&_.recommendation-reason]:tw-mb-[0.45rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-text-[0.75rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-mb-1 max-[768px]:[&_.meta-row_.ui-button]:!tw-text-[0.68rem] max-[768px]:[&_.meta-row_.ui-button]:!tw-p-[0.27rem_0.42rem] max-[768px]:[&_.course-footer]:tw-pt-[0.42rem] max-[768px]:[&_.course-footer]:tw-items-center max-[768px]:[&_.course-price]:tw-text-[0.82rem] max-[768px]:[&_.watch-now-btn]:tw-min-w-[0] max-[768px]:[&_.watch-now-btn]:tw-text-[0.72rem] max-[768px]:[&_.watch-now-btn]:tw-p-[0.3rem_0.5rem] max-[768px]:[&_.watch-now-btn]:tw-gap-[0.2rem] mobile-horizontal-item"}>
                        <CourseCatalogCard
                          course={course}
                          {...getCatalogCardState(course)}
                          reserveProgressSpace={ownedCoursesStatus === 'ready' && category.courses.some((item) => ownedCourses.has(String(item.id)))}
                          onPrimaryAction={(selectedCourse) => handleCourseClick(selectedCourse, 'course_catalog')}
                          isPrimaryLoading={clickedCourseId === course.id}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                  {showPopularNav && (
                    <button
                      type="button"
                      className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-right-0 mobile-carousel-nav mobile-carousel-nav--right"}
                      onClick={() =>
                        scrollCourseSection(
                          { current: popularCategoryScrollRefs.current[category.categoryName] },
                          'right'
                        )
                      }
                      aria-label={`Scroll popular ${category.categoryName} courses right`}
                    >
                      <Icon icon="mdi:chevron-right" />
                    </button>
                  )}
                </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {showCatalogBlock && (
        <div className="tw-p-[0.85rem] tw-border-[1px] tw-border-solid tw-border-[#e8edf8] tw-rounded-[18px] [background:linear-gradient(180deg,_#f8fbff_0%,_#ffffff_100%)] recommended-course-section tw-mb-6">
          <div className="tw-mb-2">
            <h6 className="![font-weight:var(--font-weight-semibold)] tw-mb-0 tw-font-semibold">All Courses</h6>
          </div>

          {isLoading && filteredCatalogCourses.length === 0 && (
            <div className="tw-flex tw-justify-center tw-items-center tw-w-full" style={{ minHeight: '220px' }}>
              <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                <span className="tw-sr-only">Loading...</span>
              </div>
            </div>
          )}

          {loadError && filteredCatalogCourses.length === 0 && !isLoading && (
            <NoData
              message="Courses are temporarily unavailable"
              description="Please try again in a moment."
              icon="mdi:cloud-alert-outline"
              onRetry={() => fetchCourses(1, true, searchTerm)}
            />
          )}

          {!loadError && filteredCatalogCourses.length === 0 && !isLoading && (
            <div className="tw-flex tw-justify-center tw-items-center tw-w-full" style={{ minHeight: '300px' }}>
              <NoData message="No courses found." />
            </div>
          )}

          {filteredCatalogCourses.length > 0 && (
            <div className="tw-relative tw-p-[0_0.9rem] mobile-carousel-shell">
              {showCatalogNav && (
                <button
                  type="button"
                  className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-left-0 mobile-carousel-nav mobile-carousel-nav--left"}
                  onClick={() => scrollCourseSection(catalogScrollRef, 'left')}
                  aria-label="Scroll all courses left"
                >
                  <Icon icon="mdi:chevron-left" />
                </button>
              )}
              <div className={"tw-m-0 tw-flex tw-gap-3 tw-items-stretch tw-justify-start [scroll-behavior:smooth] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden max-[768px]:tw-pb-[0.3rem] max-[768px]:tw-pl-0 max-[768px]:tw-pr-0 tw-flex-wrap tw-overflow-x-visible tw-gap-y-4 max-[768px]:tw-gap-[0.6rem] mobile-horizontal-grid catalog-courses-grid"} ref={catalogScrollRef}>
                {filteredCatalogCourses.map((course, index) => (
                  <div
                    key={`catalog-${course.id}`}
                    ref={index === filteredCatalogCourses.length - 1 ? lastCourseRef : null}
                    className={"tw-min-w-[0] [&_.course-card]:tw-h-full max-[768px]:[flex:0_0_calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-max-w-[calc((100%_-_0.6rem)_/_2)] max-[768px]:tw-min-w-[0] max-[768px]:tw-p-0 [flex:0_0_calc((100%_-_2.25rem)_/_4)] tw-w-[calc((100%_-_2.25rem)_/_4)] tw-max-w-[calc((100%_-_2.25rem)_/_4)] max-[768px]:tw-mb-0 max-[768px]:[&_.course-card]:tw-rounded-[12px] max-[768px]:[&_.course-card]:tw-min-h-full max-[768px]:[&_.course-card]:tw-flex max-[768px]:[&_.course-card]:tw-flex-col max-[768px]:[&_.course-card]:tw-w-full max-[768px]:[&_.course-card--compact_.course-image]:tw-pt-[46%] max-[768px]:[&_.course-card--compact_.course-details]:tw-p-[0.55rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-text-[0.82rem] max-[768px]:[&_.course-card--compact_.course-title]:tw-mb-[0.3rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-text-[0.75rem] max-[768px]:[&_.course-card--compact_.course-description]:tw-leading-[1.35] max-[768px]:[&_.course-card--compact_.course-description]:tw-h-[3.95em] max-[768px]:[&_.course-card--compact_.course-description]:tw-mb-[0.45rem] max-[768px]:[&_.recommendation-reason]:tw-text-[0.72rem] max-[768px]:[&_.recommendation-reason]:tw-leading-[1.35] max-[768px]:[&_.recommendation-reason]:tw-p-[0.45rem_0.5rem] max-[768px]:[&_.recommendation-reason]:tw-mb-[0.45rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-text-[0.75rem] max-[768px]:[&_.course-meta-info_.meta-row]:tw-mb-1 max-[768px]:[&_.meta-row_.ui-button]:!tw-text-[0.68rem] max-[768px]:[&_.meta-row_.ui-button]:!tw-p-[0.27rem_0.42rem] max-[768px]:[&_.course-footer]:tw-pt-[0.42rem] max-[768px]:[&_.course-footer]:tw-items-center max-[768px]:[&_.course-price]:tw-text-[0.82rem] max-[768px]:[&_.watch-now-btn]:tw-min-w-[0] max-[768px]:[&_.watch-now-btn]:tw-text-[0.72rem] max-[768px]:[&_.watch-now-btn]:tw-p-[0.35rem_0.48rem] max-[768px]:[&_.watch-now-btn]:tw-gap-1 mobile-horizontal-item catalog-courses-item"}
                  >
                    <CourseCatalogCard
                      course={course}
                      {...getCatalogCardState(course)}
                      reserveProgressSpace={catalogHasEnrolledCourses}
                      onPrimaryAction={(selectedCourse) => handleCourseClick(selectedCourse, 'course_catalog', index + 1)}
                      isPrimaryLoading={clickedCourseId === course.id}
                      compact
                    />
                  </div>
                ))}
              </div>
              {showCatalogNav && (
                <button
                  type="button"
                  className={"tw-inline-flex tw-absolute tw-top-[50%] [transform:translateY(-50%)] tw-z-[3] tw-w-[34px] tw-h-[34px] tw-rounded-[50%] tw-border-[1px] tw-border-solid tw-border-[rgba(49,_82,_232,_0.28)] [background:rgba(255,_255,_255,_0.92)] tw-text-[#3152e8] tw-items-center tw-justify-center tw-shadow-[0_10px_20px_rgba(15,_23,_42,_0.12)] [&_svg]:tw-text-[1.25rem] tw-right-0 mobile-carousel-nav mobile-carousel-nav--right"}
                  onClick={() => scrollCourseSection(catalogScrollRef, 'right')}
                  aria-label="Scroll all courses right"
                >
                  <Icon icon="mdi:chevron-right" />
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="tw-text-center tw-mt-4 tw-mb-0">Loading more courses...</p>}
        </div>
      )}
    </div>
  );
}

export default CourseTab;
